import csrfCookie from "@/util/csrfCookie";

export default async function ApiDrFinder({ formId, data }) {
  const url = process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : ''; // test url
  let csrfToken, tokenResponse = null;
  const noServerErrorMessage = 'NO SERVER ERROR: Server is not running. This error is expected if running in development mode. Demo data is being displayed instead. Some features may not work as expected.';
  const searchHistory = document.cookie
    .split('; ')
    .find(row => row.startsWith('dr_f_s_h='))
    ?.split('=')[1];
  
  if (searchHistory && data.zip_code) {
    try {
      const parsedHistory = JSON.parse(searchHistory);
      const { lat, lng } = parsedHistory[data.zip_code] || {};
      // Do something with the parsed search history
      if (parsedHistory[data.zip_code]) {
        // If the zip code exists in the history, use it
        data = { ...data, cached_lat: lat, cached_lng: lng };
      }
    } catch (error) {
      console.error('Failed to parse search history:', error.message);
    }
  }

  try {
    csrfToken = csrfCookie();
  } catch (csrfError) {
    if (process.env.NODE_ENV === 'production') {
      const error = new Error(`Authentication failed: ${csrfError.message}`);
      error['cause'] = {
        type: 'CSRF_ERROR',
        code: 'CSRF_ERROR_NO_TOKEN'
      };
      throw error;
    } else {
      const error = new Error(noServerErrorMessage);
      error['cause'] = {
        type: 'NO_SERVER_ERROR',
        code: 'NO_SERVER_ERROR_IN_DEV_MODE'
      };
      throw error;
    }
  }

  const getDrFinderBearerToken = async () => {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Csrf-Token': csrfToken, // CSRF token for security
      },
    };

    return await fetch(`${url}/api/dr-finder/get-token`, requestOptions)
      .then(response => response.json())
      .then(data => {
          return data;
      })
      .catch(error => console.error('Error:', error));
  };

  try {
    tokenResponse = await getDrFinderBearerToken();

    if (!tokenResponse) {
      const errorMessage = `AUTHENTICATION ERROR: Authentication failed.`;
      const error = new Error(errorMessage);
      error['cause'] = {
        type: 'AUTHENTICATION_ERROR',
        code: 'AUTHENTICATION_ERROR_INVALID_TOKEN'
      };
      throw error;
    }
  } catch (error) {
    console.error('Error fetching token:', error);
    if (process.env.NODE_ENV === 'production') {
      const errorMessage = `AUTHENTICATION ERROR: Get token failed: ${error.message}`;
      const newError = new Error(errorMessage);
      newError['cause'] = {
        type: 'AUTHENTICATION_ERROR',
        code: 'AUTHENTICATION_ERROR_GET_TOKEN_FAILED'
      };
      throw newError;
    } else {
      const error = new Error(noServerErrorMessage);
      error['cause'] = {
        type: 'NO_SERVER_ERROR',
        code: 'NO_SERVER_ERROR_IN_DEV_MODE'
      };
      throw error;
    }
  }

  try {
    const response = await fetch(`${url}/api/dr-finder/find`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization-Proxy': tokenResponse, // Use the token for authorization
        'X-Csrf-Token': csrfToken, // CSRF token for security
      },
      body: JSON.stringify({ formId, data }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      const error = new Error(responseData?.message || `Request failed with status ${response.status}`);
      /** @type {any} */ (error).response = { data: responseData };
      throw error;
    }

    // console.log('DR Finder API response:', responseData);

    // store geolocation for later use
    if (responseData?.g && responseData.g?.lat && responseData.g?.lng && data?.zip_code) {
      // file deepcode ignore WebCookieSecureDisabledByDefault: Secure is set
      const parsedHistory = searchHistory ? JSON.parse(searchHistory) : null;
      const allHistory = { ...parsedHistory, [data.zip_code]: { lat: responseData.g.lat, lng: responseData.g.lng } };
      document.cookie = `dr_f_s_h=${JSON.stringify(allHistory)}; path=/; secure=true`;
    }

    return {
      success: true,
      response: responseData,
    };
  } catch (error) {
    // console.log('Error fetching DR Finder:', error);
    const err = /** @type {any} */ (error);
    return {
      success: false,
      error: {
        name: err?.response?.data?.name || err?.name || 'Error',
        message: err?.response?.data?.message || err?.message || 'An unknown error occurred',
        cause: err?.response?.data?.cause,
      }
    };
  }
}
